'use client'

import { OverDialogLayer } from '@fukku/dialog/OverDialogLayer/OverDialogLayer'
import text from '@fukku/styles/texts.module.scss'
import type { IconProps } from 'icons/types/Icon.types'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext/useNotificationsContext'
import { useSnackbar } from 'notifications/hooks/useSnackbar/useSnackbar'
import {
	type ForwardedRef,
	forwardRef,
	type MouseEvent,
	type ReactElement,
	type ReactNode,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react'
import { cx } from 'utils/cx'
import { SnackbarPosition, SnackbarStatus } from '../../constants/constants'
import { LargeSnackbar } from './LargeSnackbar/LargeSnackbar'
import styles from './Snackbar.module.scss'

export interface SnackbarHandler {
	open: () => void
	close: () => void
	toggle: () => void
}

export type SnackbarType = 'default' | 'large'

export interface SnackbarProps {
	readonly dataTestId?: string
	readonly delay?: number
	readonly icon?: ReactElement<IconProps>
	readonly id: string
	readonly status?: SnackbarStatus
	readonly position?: SnackbarPosition
	readonly subtitle?: string
	readonly buttonText?: string
	readonly title: string
	readonly type?: SnackbarType
	readonly onClick?: (e: MouseEvent<HTMLButtonElement>) => void
	readonly onClose?: () => void
	readonly children?: ReactNode
	readonly isSmall?: boolean
}

const DEFAULT_DELAY = 5000
const ANIMATION_DELAY = 400

export const Snackbar = forwardRef<SnackbarHandler, SnackbarProps>(
	function Snackbar(
		{
			delay = DEFAULT_DELAY,
			icon,
			id,
			status = SnackbarStatus.INFO,
			position = SnackbarPosition.BOTTOM,
			subtitle,
			dataTestId,
			buttonText,
			title,
			type = 'default',
			onClick,
			onClose,
			children,
			isSmall,
		}: SnackbarProps,
		ref: ForwardedRef<SnackbarHandler | null>
	): ReactNode | null {
		const realPosition = isSmall ? position : SnackbarPosition.TOP

		const {
			snackbarRef,
			snackbarStyle,
			handleIn,
			handleOut,
			handleClick,
			handleOnAnimationEnd,
		} = useSnackbar({
			delay,
			onClick,
			onClose,
			position: realPosition,
		})
		const { snackbarShow, setSnackbarShow, removeNotification } =
			useNotificationsContext()
		const isButton = !!onClick && !!buttonText
		const [opened, setOpened] = useState(false)

		useImperativeHandle(
			ref,
			() =>
				({
					open: () => setSnackbarShow(true),
					close: () => setSnackbarShow(false),
					toggle: () => setSnackbarShow(!snackbarShow),
				}) satisfies SnackbarHandler,
			[]
		)

		const isLargeSnackbar = type === 'large' && snackbarShow && children

		useEffect(() => {
			if (snackbarShow) {
				setTimeout(() => {
					setOpened(true)
				}, ANIMATION_DELAY)
			}
			return () => {
				setOpened(false)
			}
		}, [snackbarShow])

		const handleTouchStart = (event: React.TouchEvent<HTMLButtonElement>) => {
			if (event.cancelable) {
				event.preventDefault()
			}
			if (onClick) {
				onClick(event as unknown as MouseEvent<HTMLButtonElement>)
			}
		}

		if (isLargeSnackbar) {
			return (
				<LargeSnackbar
					id={id}
					title={title}
					status={status}
					position={realPosition}
					snackbarRef={snackbarRef}
					snackbarStyle={snackbarStyle}
					handleIn={handleIn}
					handleOut={handleOut}
					handleOnAnimationEnd={handleOnAnimationEnd}
					dataTestId={dataTestId}
					onClose={onClose}
					opened={opened}
					removeNotification={removeNotification}
				>
					{children}
				</LargeSnackbar>
			)
		}

		if (snackbarShow) {
			return (
				<OverDialogLayer>
					<div
						id={`${id}-snackbar`}
						className={cx(
							styles.snackbarWrapper,
							!opened && styles.open,
							styles[status],
							position === SnackbarPosition.TOP && styles.smallTop
						)}
						ref={snackbarRef}
						onAnimationEnd={handleOnAnimationEnd}
						style={snackbarStyle}
						role='alert'
						data-testid={
							dataTestId ? `${dataTestId}-snackbar` : 'snackbar-notification'
						}
					>
						<div
							className={styles.snackbarElement}
							onPointerEnter={handleIn}
							onPointerLeave={handleOut}
						>
							<div className={styles.wrapperElements}>
								{icon}

								<div className={styles.wrapperText}>
									<p className={cx(text.titleM, styles.title)}>{title}</p>
									{subtitle && (
										<p className={cx(text.bodyM, styles.subtitle)}>
											{subtitle}
										</p>
									)}
								</div>
							</div>
						</div>

						{isButton && (
							<button
								id={`${id}-button`}
								type='button'
								className={cx(text.titleM, styles.button)}
								onClick={handleClick}
								onTouchStart={handleTouchStart}
								onFocus={handleIn}
								onBlur={handleOut}
								data-testid={dataTestId ? `${dataTestId}-button` : null}
							>
								<p className={cx(styles.buttonText)}>{buttonText}</p>
							</button>
						)}
					</div>
				</OverDialogLayer>
			)
		}

		return null
	}
)
